import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";

/**
 * Seleção EXPLÍCITA de módulos no cadastro de usuário (negação por padrão).
 *
 * O backend grava somente o que for marcado aqui (authorization_model=2).
 * A lista mostra os módulos contratados pelo(s) cliente(s) selecionado(s)
 * — teto do tenant — pré-marcando o modelo do perfil. O backend revalida
 * teto e autoridade de quem cria; esta tela não decide acesso.
 */
export default function UserModuleGrants({ role, clientIds = [], value, onChange }) {
  const [labels, setLabels] = useState({});
  const [template, setTemplate] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const clientKey = (clientIds || []).filter(Boolean).join(",");

  useEffect(() => {
    api
      .get("/meta")
      .then(({ data }) => setLabels(data?.modules || {}))
      .catch(() => setLabels({}));
  }, []);

  useEffect(() => {
    if (!role) {
      setTemplate(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .get("/users/access/templates", { params: { role, client_ids: clientKey } })
      .then(({ data }) => {
        if (cancelled) return;
        setTemplate(data);
        // Pré-marca o modelo do perfil sempre que perfil/clientes mudam.
        onChange?.({ ...(data?.template_modules || {}) });
      })
      .catch((err) => {
        if (cancelled) return;
        setTemplate(null);
        setError(formatApiErrorDetail(err.response?.data?.detail));
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // onChange é estável no chamador; recalcular só por perfil/clientes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, clientKey]);

  const available = useMemo(() => {
    const ceiling = template?.tenant_ceiling;
    if (ceiling?.member_of_client) {
      return Object.entries(ceiling.modules || {})
        .filter(([, enabled]) => enabled)
        .map(([key]) => key);
    }
    return Object.keys(template?.template_modules || {});
  }, [template]);

  const selected = value || {};
  const toggle = (key, checked) => onChange?.({ ...selected, [key]: !!checked });

  return (
    <section className="space-y-2" aria-labelledby="user-module-grants-title" data-testid="user-module-grants">
      <div className="flex items-center justify-between">
        <h3 id="user-module-grants-title" className="text-sm font-semibold">
          Módulos liberados para o usuário
        </h3>
        <Badge variant="outline" className="text-[10px]">
          {Object.values(selected).filter(Boolean).length} selecionado(s)
        </Badge>
      </div>
      <p className="text-xs text-muted-foreground">
        Somente os módulos marcados ficam disponíveis. A lista respeita os módulos contratados pelo cliente.
      </p>

      {loading && <p className="text-xs text-muted-foreground">Carregando módulos do perfil…</p>}
      {error && <p className="text-xs text-destructive">{error}</p>}
      {!loading && !error && template && available.length === 0 && (
        <p className="text-xs text-muted-foreground" data-testid="user-module-grants-empty">
          O cliente selecionado não possui módulos contratados. Peça à administração da plataforma para liberá-los.
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-56 overflow-y-auto pr-1">
        {available.map((key) => (
          <label
            key={key}
            className="flex items-center gap-2 rounded-md border border-border px-2.5 py-1.5 text-sm cursor-pointer hover:bg-muted/40"
          >
            <Checkbox
              checked={!!selected[key]}
              onCheckedChange={(checked) => toggle(key, checked)}
              data-testid={`user-module-grant-${key}`}
            />
            <span>{labels[key] || key}</span>
          </label>
        ))}
      </div>
    </section>
  );
}
